import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Autocomplete,
  Menu, ListItemIcon, ListItemText, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon,   Print as PrintIcon, Share as ShareIcon,
  Cancel as CancelIcon, MoreVert as MoreIcon, Receipt as ReceiptIcon,
  AutoAwesome as AiIcon, Person as PersonIcon, MedicalServices as MedicalIcon,
  Description as NfseIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

import SendDocumentDialog from '../../components/SendDocumentDialog';

const statusLabels: Record<string, string> = { issued: 'Emitido', cancelled: 'Cancelado' };
const statusColors: Record<string, string> = { issued: '#22c55e', cancelled: '#ef4444' };
const paymentLabels: Record<string, string> = {
  cash: 'Dinheiro', credit_card: 'Cartão Crédito', debit_card: 'Cartão Débito',
  pix: 'PIX', bank_transfer: 'Transferência', check: 'Cheque', insurance: 'Convênio', other: 'Outro',
};

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

function escapeHtml(s: string | undefined | null): string {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** clinic_settings (mesmos campos que orçamento/receituário) */
function buildReceiptClinicBlockHtml(c: any): string {
  if (!c) {
    return `
      <div class="doc-head-center">
        <div class="clinic-name">Clínica</div>
        <div class="clinic-subtitle">Configure nome, endereço e logo em Clínica › Configurações</div>
      </div>
      <div class="doc-head-meta"></div>`;
  }
  const logo = c.logo_url
    ? `<div class="doc-head-logo"><img src="${escapeHtml(c.logo_url)}" alt="" /></div>`
    : '<div class="doc-head-logo doc-head-logo--empty"></div>';
  const subtitle = c.clinic_subtitle
    ? `<div class="clinic-subtitle">${escapeHtml(c.clinic_subtitle)}</div>`
    : '';
  const cnpjLine = c.cnpj ? `<div>CNPJ: ${escapeHtml(c.cnpj)}</div>` : '';
  let addr = '';
  if (c.address_street) {
    addr = `<div>${escapeHtml(c.address_street)}${c.address_number ? `, ${escapeHtml(c.address_number)}` : ''}${c.address_neighborhood ? ` — ${escapeHtml(c.address_neighborhood)}` : ''}</div>`;
  }
  const cityLine =
    c.address_city || c.address_state || c.address_zip
      ? `<div>${[c.address_city, c.address_state].filter(Boolean).map(escapeHtml).join(' - ')}${c.address_zip ? ` • CEP ${escapeHtml(c.address_zip)}` : ''}</div>`
      : '';
  const tel =
    c.phone || c.whatsapp ? `<div>Tel: ${escapeHtml(c.phone || c.whatsapp)}</div>` : '';
  const mail = c.email ? `<div>${escapeHtml(c.email)}</div>` : '';
  const site = c.website ? `<div>${escapeHtml(c.website)}</div>` : '';

  return `
    ${logo}
    <div class="doc-head-center">
      <div class="clinic-name">${escapeHtml(c.clinic_name || 'Clínica')}</div>
      ${subtitle}
    </div>
    <div class="doc-head-meta">
      ${cnpjLine}
      ${addr}
      ${cityLine}
      ${tel}
      ${mail}
      ${site}
    </div>`;
}

const Receipts: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const [receipts, setReceipts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<any>(null);
  const [cancelDialog, setCancelDialog] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');

  // AI receipt states
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiServiceDesc, setAiServiceDesc] = useState('');
  const [aiError, setAiError] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const [form, setForm] = useState<any>({
    patient_id: '', professional_id: '', issue_date: new Date().toISOString().split('T')[0],
    amount: '', description: '', services_description: '', payment_method: 'pix', notes: '',
  });
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profOptions, setProfOptions] = useState<any[]>([]);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadReceipts(); }, [search, filterStatus]);

  const loadData = async () => { await Promise.all([loadReceipts(), loadStats()]); };

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 50 };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      const { data } = await axios.get(getApiUrl('/clinic/receipts'), { params });
      setReceipts(data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/receipts/stats'));
      setStats(data.data || {});
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: q } });
      setPatientOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: q } });
      setProfOptions(data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(getApiUrl(`/clinic/receipts/${editing.id}`), form);
        setSnackbar({ open: true, message: 'Recibo atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/receipts'), form);
        setSnackbar({ open: true, message: 'Recibo emitido!', severity: 'success' });
      }
      setOpenDialog(false); resetForm(); loadData();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    }
  };

  const handleCancel = async () => {
    if (!cancelDialog) return;
    try {
      await axios.post(getApiUrl(`/clinic/receipts/${cancelDialog.id}/cancel`), { reason: cancelReason });
      setSnackbar({ open: true, message: 'Recibo cancelado!', severity: 'success' });
      setCancelDialog(null); setCancelReason(''); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao cancelar', severity: 'error' }); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir recibo?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/receipts/${id}`));
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' }); loadData();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handlePrint = async (id: string) => {
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/receipts/${id}/print`));
      const r = data.data.receipt;
      const c = data.data.clinic;
      const w = window.open('', '_blank');
      if (!w) return;

      const issueStr = r?.issue_date ? new Date(r.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '';
      const patientName = r?.patient_snapshot?.full_name || 'N/A';
      const patientCpf = r?.patient_snapshot?.cpf;
      const profName = r?.professional_snapshot?.full_name || '';
      const profReg =
        [r?.professional_snapshot?.registration_type, r?.professional_snapshot?.registration_number].filter(Boolean).join(' ')
          || '';
      const cancelled = r?.status === 'cancelled';

      const profSigUrl = (r?.professional_snapshot?.signature_image_url || '').trim();
      const profSigHtml = profSigUrl
        ? `<div class="prof-sig-print"><img src="${escapeHtml(profSigUrl)}" alt="" /></div>`
        : '';

      const servicesBlock = r?.services_description
        ? `<div class="section"><div class="section-title">Detalhamento dos serviços</div><div class="field multiline">${escapeHtml(r.services_description).replace(/\n/g, '<br/>')}</div></div>`
        : '';

      const notesBlock = r?.notes
        ? `<div class="section"><div class="section-title">Observações</div><div class="field multiline">${escapeHtml(r.notes).replace(/\n/g, '<br/>')}</div></div>`
        : '';

      w.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8"><title>Recibo ${escapeHtml(r?.receipt_number || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1f2937; margin: 0; padding: 24px; background: #f8fafc; }
  .sheet { max-width: 210mm; margin: 0 auto; background: #fff; padding: 28px 32px 36px; border-radius: 12px; box-shadow: 0 1px 3px rgba(15,23,42,0.08); }
  .doc-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; border-bottom: 3px solid #0d9488; padding-bottom: 16px; margin-bottom: 20px; }
  .doc-head-logo { flex-shrink: 0; min-width: 120px; }
  .doc-head-logo img { display: block; max-width: 160px; max-height: 64px; width: auto; height: auto; object-fit: contain; object-position: left top; }
  .doc-head-logo--empty { min-height: 48px; }
  .doc-head-center { flex: 1; min-width: 0; text-align: center; align-self: center; }
  .doc-head-meta { flex-shrink: 0; max-width: 44%; font-size: 10px; color: #6b7280; text-align: right; line-height: 1.5; }
  .clinic-name { font-size: 20px; font-weight: 700; color: #0d9488; margin: 0; }
  .clinic-subtitle { font-size: 12px; color: #6b7280; margin-top: 4px; }
  .doc-badge { text-align: center; margin-bottom: 8px; }
  .doc-badge span { display: inline-block; padding: 6px 18px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; background: #f0fdf4; color: #0d9488; border: 1px solid #a7f3d0; }
  .doc-badge.cancelled span { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
  .doc-title { text-align: center; font-size: 17px; font-weight: 700; color: #0f172a; margin: 0 0 4px; }
  .doc-number { text-align: center; font-size: 12px; color: #64748b; margin-bottom: 18px; }
  .amount-wrap { text-align: center; margin: 18px 0 10px; padding: 18px 16px; background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%); border: 1px solid #a7f3d0; border-radius: 12px; }
  .amount { font-size: 30px; font-weight: 800; color: #0d9488; letter-spacing: -0.02em; }
  .amount-words { font-size: 13px; color: #475569; font-style: italic; margin-top: 8px; line-height: 1.4; }
  .section { margin-bottom: 16px; }
  .section-title { font-size: 11px; font-weight: 700; color: #0d9488; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e5e7eb; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 20px; }
  .field { font-size: 13px; line-height: 1.5; color: #334155; }
  .field strong { color: #0f172a; font-weight: 600; }
  .field.multiline { white-space: normal; }
  .signatures { display: flex; justify-content: space-between; gap: 24px; margin-top: 48px; padding-top: 8px; }
  .signature-line { flex: 1; text-align: center; }
  .signature-line hr { border: none; border-top: 1px solid #334155; margin: 0 0 8px; }
  .signature-line .name { font-size: 13px; font-weight: 600; color: #0f172a; }
  .signature-line .reg { font-size: 11px; color: #64748b; margin-top: 2px; }
  .prof-sig-print { text-align: center; margin-bottom: 6px; min-height: 0; }
  .prof-sig-print img { max-height: 90px; max-width: 360px; object-fit: contain; display: inline-block; vertical-align: bottom; }
  .footer-note { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #e2e8f0; font-size: 10px; color: #94a3b8; text-align: center; line-height: 1.5; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; border-radius: 0; max-width: none; }
    @page { size: A4; margin: 14mm; }
  }
</style></head><body>
<div class="sheet">
  <div class="doc-head">${buildReceiptClinicBlockHtml(c)}</div>
  <div class="doc-badge${cancelled ? ' cancelled' : ''}"><span>${cancelled ? 'Recibo cancelado' : 'Recibo de pagamento'}</span></div>
  <h1 class="doc-title">${cancelled ? 'RECIBO (cancelado)' : 'RECIBO DE PAGAMENTO'}</h1>
  <div class="doc-number">Nº ${escapeHtml(r?.receipt_number || '—')} ${issueStr ? `• Emissão: ${escapeHtml(issueStr)}` : ''}</div>
  <div class="amount-wrap">
    <div class="amount">${formatCurrency(r?.amount || 0)}</div>
    ${r?.amount_in_words ? `<div class="amount-words">(${escapeHtml(r.amount_in_words)})</div>` : ''}
  </div>
  <div class="section">
    <div class="section-title">Pagador</div>
    <div class="info-grid">
      <div class="field"><strong>Recebi de:</strong> ${escapeHtml(patientName)}</div>
      ${patientCpf ? `<div class="field"><strong>CPF:</strong> ${escapeHtml(patientCpf)}</div>` : '<div></div>'}
    </div>
  </div>
  <div class="section">
    <div class="section-title">Referência e pagamento</div>
    <div class="field"><strong>Referente a:</strong> ${escapeHtml(r?.description || '—')}</div>
    <div class="field" style="margin-top:8px"><strong>Forma de pagamento:</strong> ${escapeHtml(paymentLabels[r?.payment_method] || r?.payment_method || '—')}</div>
  </div>
  ${servicesBlock}
  ${notesBlock}
  <div class="signatures">
    <div class="signature-line">
      ${profSigHtml}
      <hr />
      <div class="name">${escapeHtml(profName || c?.clinic_name || 'Profissional')}</div>
      ${profReg ? `<div class="reg">${escapeHtml(profReg)}</div>` : ''}
    </div>
    <div class="signature-line">
      <hr />
      <div class="name">${escapeHtml(patientName)}</div>
      ${patientCpf ? `<div class="reg">CPF ${escapeHtml(patientCpf)}</div>` : '<div class="reg">Pagador</div>'}
    </div>
  </div>
  <div class="footer-note">Documento emitido eletronicamente. Em caso de dúvidas, entre em contato com a clínica pelos dados no cabeçalho.</div>
</div>
</body></html>`);
      w.document.close();
      setTimeout(() => w.print(), 400);
    } catch (e) { setSnackbar({ open: true, message: 'Erro ao imprimir', severity: 'error' }); }
  };

  const handleEmitNfse = async (receiptId: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/nfse/from-receipt/${receiptId}`));
      setSnackbar({ open: true, message: 'NFSe enviada para processamento! Acompanhe em Notas Fiscais.', severity: 'success' });
      setMenuAnchor(null); setMenuItem(null);
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao emitir NFSe', severity: 'error' });
    }
  };

  const handleEdit = (r: any) => {
    setEditing(r);
    setForm({
      patient_id: r.patient_id || '', professional_id: r.professional_id || '',
      issue_date: r.issue_date || new Date().toISOString().split('T')[0],
      amount: r.amount || '', description: r.description || '',
      services_description: r.services_description || '',
      payment_method: r.payment_method || 'pix', notes: r.notes || '',
    });
    if (r.patient_name) setPatientOptions([{ id: r.patient_id, full_name: r.patient_name }]);
    if (r.professional_name) setProfOptions([{ id: r.professional_id, full_name: r.professional_name }]);
    setOpenDialog(true);
  };

  const resetForm = () => {
    setForm({
      patient_id: '', professional_id: '', issue_date: new Date().toISOString().split('T')[0],
      amount: '', description: '', services_description: '', payment_method: 'pix', notes: '',
    });
    setPatientOptions([]); setProfOptions([]);
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiServiceDesc.trim()) {
      setAiError('Preencha o nome do paciente e a descrição do serviço');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/receipts/ai-generate'), {
        patient_name: aiPatientName.trim(),
        service_description: aiServiceDesc.trim(),
      });

      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');

      const rawText = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.receipt || JSON.stringify(aiData));
      const parsed = parseAiReceipt(rawText, aiServiceDesc.trim());

      resetForm();
      setForm((f: any) => ({
        ...f,
        description: parsed.description || aiServiceDesc.trim(),
        services_description: parsed.services_description || '',
        amount: parsed.amount || '',
        payment_method: parsed.payment_method || 'pix',
        notes: parsed.notes || '',
      }));

      setAiDialogOpen(false);
      setEditing(null);
      setOpenDialog(true);
      setSnackbar({ open: true, message: 'Recibo gerado com IA! Revise e ajuste antes de emitir.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar recibo com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const parseAiReceipt = (text: string, fallbackDesc: string) => {
    const result: any = { description: '', services_description: '', amount: '', payment_method: '', notes: '' };
    const lines = text.split('\n').map((l: string) => l.trim()).filter((l: string) => l);
    const servicesLines: string[] = [];
    let inServices = false;

    for (const line of lines) {
      const lower = line.toLowerCase();

      if (lower.startsWith('descrição:') || lower.startsWith('descricao:') || lower.startsWith('referente a:') || lower.startsWith('referente:')) {
        result.description = line.split(':').slice(1).join(':').trim();
        inServices = false;
        continue;
      }
      if (lower.startsWith('valor:') || lower.startsWith('valor total:') || lower.startsWith('total:')) {
        const val = line.split(':').slice(1).join(':').trim().replace(/[R$\s.]/g, '').replace(',', '.');
        const num = parseFloat(val);
        if (!isNaN(num)) result.amount = num;
        inServices = false;
        continue;
      }
      if (lower.startsWith('forma de pagamento:') || lower.startsWith('pagamento:') || lower.startsWith('método de pagamento:') || lower.startsWith('metodo de pagamento:')) {
        const val = line.split(':').slice(1).join(':').trim().toLowerCase();
        if (val.includes('dinheiro') || val.includes('cash') || val.includes('espécie')) result.payment_method = 'cash';
        else if (val.includes('crédito') || val.includes('credito') || val.includes('credit')) result.payment_method = 'credit_card';
        else if (val.includes('débito') || val.includes('debito') || val.includes('debit')) result.payment_method = 'debit_card';
        else if (val.includes('pix')) result.payment_method = 'pix';
        else if (val.includes('transferência') || val.includes('transferencia')) result.payment_method = 'bank_transfer';
        else if (val.includes('cheque')) result.payment_method = 'check';
        else if (val.includes('convênio') || val.includes('convenio') || val.includes('seguro')) result.payment_method = 'insurance';
        else result.payment_method = 'other';
        inServices = false;
        continue;
      }
      if (lower.startsWith('observaç') || lower.startsWith('observac') || lower.startsWith('notas:') || lower.startsWith('nota:')) {
        result.notes = line.split(':').slice(1).join(':').trim();
        inServices = false;
        continue;
      }
      if (lower.startsWith('serviços:') || lower.startsWith('servicos:') || lower.startsWith('detalhamento:') || lower.startsWith('serviços prestados:') || lower.startsWith('servicos prestados:')) {
        inServices = true;
        const afterColon = line.split(':').slice(1).join(':').trim();
        if (afterColon) servicesLines.push(afterColon);
        continue;
      }

      if (inServices) {
        servicesLines.push(line);
      }
    }

    if (servicesLines.length > 0) result.services_description = servicesLines.join('\n');
    if (!result.description) result.description = fallbackDesc;

    return result;
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Recibos Fiscais</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Emita e gerencie recibos de pagamento</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadData} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
          <Button variant="outlined" startIcon={<AiIcon />}
            onClick={() => { setAiPatientName(''); setAiServiceDesc(''); setAiError(''); setAiDialogOpen(true); }}
            sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(139,92,246,0.04)' }, textTransform: 'none', fontWeight: 600 }}>
            Criar com IA
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }} onClick={() => { setEditing(null); resetForm(); setOpenDialog(true); }}>
            Novo Recibo
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Emitidos', value: stats.total || 0, color: '#14B8A6' },
          { label: 'Este Mês', value: stats.total_month || 0, color: '#3b82f6' },
          { label: 'Valor Total', value: formatCurrency(stats.total_amount || 0), color: '#22c55e' },
          { label: 'Valor Mês', value: formatCurrency(stats.total_amount_month || 0), color: '#8b5cf6' },
        ].map((kpi, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ bgcolor: 'white', borderRadius: 3, border: `1px solid ${kpi.color}22` }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="caption" sx={{ color: '#64748b' }}>{kpi.label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color, fontSize: typeof kpi.value === 'string' ? '1.1rem' : undefined }}>{kpi.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', p: 2, '&:last-child': { pb: 2 } }}>
          <TextField size="small" placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} sx={{ minWidth: 250 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="issued">Emitido</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nº</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pagamento</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
              ) : receipts.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum recibo</TableCell></TableRow>
              ) : receipts.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 600, color: '#14B8A6' }}>{r.receipt_number || '-'}</TableCell>
                  <TableCell>{r.patient_name || '-'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(r.amount)}</TableCell>
                  <TableCell>{paymentLabels[r.payment_method] || r.payment_method || '-'}</TableCell>
                  <TableCell><Chip label={statusLabels[r.status] || r.status} size="small"
                    sx={{ bgcolor: (statusColors[r.status] || '#666') + '15', color: statusColors[r.status] || '#666', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                  <TableCell>{r.issue_date ? new Date(r.issue_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handlePrint(r.id)} sx={{ color: '#14B8A6' }}><PrintIcon fontSize="small" /></IconButton>
                    <Tooltip title="Enviar"><IconButton size="small" onClick={() => { const p = r.patient_snapshot || {}; setSendDocData({ id: r.id, patientId: r.patient_id, email: p.email, phone: p.phone, whatsapp: p.whatsapp, name: p.full_name || r.patient_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                    {r.status !== 'cancelled' && <IconButton size="small" onClick={() => handleEdit(r)} sx={{ color: '#3b82f6' }}><EditIcon fontSize="small" /></IconButton>}
                    <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuItem(r); }}><MoreIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {menuItem?.status === 'issued' && (
          <MenuItem onClick={() => menuItem && handleEmitNfse(menuItem.id)}>
            <ListItemIcon><NfseIcon fontSize="small" sx={{ color: '#14B8A6' }} /></ListItemIcon><ListItemText>Emitir NF-e</ListItemText></MenuItem>
        )}
        {menuItem?.status !== 'cancelled' && <MenuItem onClick={() => { setCancelDialog(menuItem); setMenuAnchor(null); }}>
          <ListItemIcon><CancelIcon fontSize="small" sx={{ color: '#f59e0b' }} /></ListItemIcon><ListItemText>Cancelar Recibo</ListItemText></MenuItem>}
        <MenuItem onClick={() => { handleDelete(menuItem?.id); setMenuAnchor(null); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText></MenuItem>
      </Menu>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Recibo</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Motivo do cancelamento" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            multiline rows={3} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Voltar</Button>
          <Button variant="contained" color="error" onClick={handleCancel}>Cancelar Recibo</Button>
        </DialogActions>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{editing ? 'Editar' : 'Novo'} Recibo</Typography>
          <IconButton onClick={() => setOpenDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete size="small" options={patientOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchPatients(v)} onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                onOpen={() => searchPatients('')} value={patientOptions.find((p: any) => p.id === form.patient_id) || null}
                renderInput={(params) => <TextField {...params} label="Paciente" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete size="small" options={profOptions} getOptionLabel={(o: any) => o.full_name || ''}
                onInputChange={(_, v) => searchProfessionals(v)} onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                onOpen={() => searchProfessionals('')} value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                renderInput={(params) => <TextField {...params} label="Profissional" />}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="Valor (R$)" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pagamento</InputLabel>
                <Select value={form.payment_method} label="Forma de Pagamento" onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                  {Object.entries(paymentLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" label="Data Emissão" value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descrição / Referente a" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Consulta odontológica" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Detalhamento dos Serviços" value={form.services_description}
                onChange={(e) => setForm({ ...form, services_description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}>
            {editing ? 'Atualizar' : 'Emitir Recibo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Receipt Dialog */}
      <Dialog open={aiDialogOpen} onClose={() => !aiGenerating && setAiDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#8b5cf6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Recibo com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} disabled={aiGenerating}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              A IA irá gerar um recibo completo com descrição detalhada dos serviços, valor sugerido e forma de pagamento. Revise sempre antes de emitir.
            </Alert>
            {aiError && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{aiError}</Alert>}
          </Box>
          <TextField
            fullWidth
            label="Nome do Paciente *"
            placeholder="Ex: Maria Silva"
            value={aiPatientName}
            onChange={(e) => setAiPatientName(e.target.value)}
            disabled={aiGenerating}
            sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
          <TextField
            fullWidth
            label="Descrição do Serviço / Procedimento *"
            placeholder="Ex: Consulta odontológica com limpeza e aplicação de flúor"
            value={aiServiceDesc}
            onChange={(e) => setAiServiceDesc(e.target.value)}
            disabled={aiGenerating}
            multiline
            rows={3}
            onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey && !aiGenerating) { e.preventDefault(); handleAiGenerate(); } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><ReceiptIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPatientName.trim() || !aiServiceDesc.trim()}
            startIcon={aiGenerating ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
            sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' }, textTransform: 'none', fontWeight: 600, minWidth: 180 }}
          >
            {aiGenerating ? 'Gerando recibo...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="receipt"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.whatsapp}
        patientName={sendDocData?.name}
        documentLabel="Recibo"
      />
    </Box>
  );
};

export default Receipts;
